import { useState, type ReactNode } from 'react';
import {
  BookOutlined,
  FileTextOutlined,
  HomeOutlined,
  LineChartOutlined,
  LockOutlined,
  MenuOutlined,
  ReadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Flex, Grid, Layout, Menu, Tooltip, theme } from 'antd';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  CabecalhoDaBarra,
  Logotipo,
  PerfilDaBarra,
  RodapeDaBarra,
  useBarraRecolhida,
  type ModuleKey,
} from '@titularis/ui';
import {
  ALTURA_DO_AVISO,
  AvisoDoPlanoGratuito,
  useAvisoDoPlanoGratuito,
} from '../components/AvisoDoPlanoGratuito';
import { BoasVindas } from '../components/BoasVindas';
import { OfertaProvider } from '../components/Oferta';
import { GRANDES_MODULOS, type SubModulo } from '../grandesModulos';
import { ChamadaDoPlano } from '../components/ChamadaDoPlano';
import { useAvisoDeEmailConfirmado } from '../pages/auth/useAvisoDeEmailConfirmado';
import { signOut, useSession } from '../auth';

/**
 * Layout do aluno.
 *
 * Os módulos são o eixo do produto - cada um é uma etapa do concurso, com preço
 * próprio -, então ficam sempre à vista numa barra lateral, em vez de exigirem
 * uma volta à tela inicial a cada troca.
 *
 * A barra é fixa: rolar uma lista de centenas de questões não pode levar a
 * navegação embora. E o que é do usuário - conta, tema, sair - fica no rodapé
 * dela, não numa faixa no topo: assim a largura inteira da tela é do enunciado.
 *
 * No celular a barra vira gaveta: com 390px, uma coluna fixa comeria o espaço
 * de leitura.
 */
// O ícone de cada fase, o mesmo na barra e nos atalhos da tela de início: quem
// clica num reconhece o lugar aonde chegou pelo outro.
export const ICONE_MODULO: Record<ModuleKey, ReactNode> = {
  enac: <ReadOutlined />,
  objective: <FileTextOutlined />,
  essay: <BookOutlined />,
};

const CHAVE_RECOLHIDA = 'titularis:app:barra-recolhida';

const LARGURA = 232;
const LARGURA_RECOLHIDA = 72;

/**
 * A coluna de leitura das telas de estudo.
 *
 * Enunciado longo em linha larga cansa, então o conteúdo delas é centralizado
 * nesta largura. **Configurações é a exceção**: ela tem coluna de assuntos
 * própria, e centralizá-la deixaria as duas navegações separadas por um vão,
 * com a de dentro flutuando no meio da tela.
 */
const LARGURA_DE_LEITURA = 900;

export function AppLayout() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const telas = Grid.useBreakpoint();
  const { data: sessao } = useSession();
  const { recolhida, escolher, recolherPorTela } = useBarraRecolhida(CHAVE_RECOLHIDA);
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const aviso = useAvisoDoPlanoGratuito();

  /*
   * Quem chega pelo link do e-mail cai em alguma tela daqui de dentro, e é aqui
   * que a confirmação vira mensagem - seja qual for o destino que ela carregou.
   */
  useAvisoDeEmailConfirmado();

  // Configurações administra a própria largura e o próprio recuo.
  const telaLarga = location.pathname.startsWith('/configuracoes');
  // A faixa do topo empurra a barra lateral fixa para baixo. Sem faixa, zero.
  const recuoDoTopo = aviso.mostrar ? ALTURA_DO_AVISO : 0;

  const moduloAtual = searchParams.get('module');
  /*
   * A chave selecionada tem que casar com a que o item usa. Módulo de questões
   * é `modulo:<fase>`; módulo com tela própria - hoje a Legislação Comentada -
   * é `rota:<caminho>`, e sem este ramo ele ficava aberto sem nada aceso na
   * barra, como se a pessoa não estivesse em lugar nenhum.
   */
  const selecionado =
    location.pathname === '/estudar' && moduloAtual
      ? `modulo:${moduloAtual}`
      : location.pathname === '/'
        ? 'inicio'
        : `rota:${location.pathname}`;

  /*
   * **A barra só mostra o que existe.** Magistratura e OAB ficavam nela como
   * dois grupos com etiqueta "em breve" e cinco itens de cadeado - cinco linhas
   * cinzas, quase metade da barra, para dizer "aqui não tem nada". A Home já
   * tinha deixado de ser uma parede de "em breve" em 05/09/2026, e a barra ficou
   * sendo o último lugar que ainda vendia ausência.
   *
   * Os concursos continuam no `GRANDES_MODULOS`, com `disponivel: false`: eles
   * voltam à barra no dia em que tiverem prova cadastrada, sem nada para
   * reescrever.
   */
  const concursosNoAr = GRANDES_MODULOS.filter((concurso) => concurso.disponivel);

  const itemDoModulo = (sub: SubModulo) => {
    /*
     * Existe e ainda não abre: hoje, a 2ª fase, que está sendo comentada. O item
     * fica à vista, com cadeado, porque o aluno precisa saber que elas vêm - e
     * desabilitado, porque abrir levaria a uma lista de zero questões.
     */
    if (sub.emPreparo) {
      return {
        key: `preparo:${sub.chave}`,
        icon: <LockOutlined />,
        disabled: true,
        label: sub.nome,
      };
    }
    // Módulo com rota própria não é estudo de questões - hoje, a Legislação
    // Comentada (D-20).
    if (sub.rota) {
      return { key: `rota:${sub.rota}`, icon: <ReadOutlined />, label: sub.nome };
    }
    if (sub.moduleKey) {
      return { key: `modulo:${sub.moduleKey}`, icon: ICONE_MODULO[sub.moduleKey], label: sub.nome };
    }
    return { key: `bloqueado:${sub.chave}`, icon: <LockOutlined />, disabled: true, label: sub.nome };
  };

  const gruposDeConcurso = concursosNoAr.map((concurso) => ({
    key: `grupo:${concurso.chave}`,
    type: 'group' as const,
    label: concurso.nome,
    children: concurso.subModulos.map((sub) => itemDoModulo(sub)),
  }));

  // Recolhida, os grupos não cabem: os módulos viram uma fila de ícones.
  const itensAchatados = concursosNoAr.flatMap((concurso) =>
    concurso.subModulos.map((sub) => itemDoModulo(sub)),
  );

  const sair = async () => {
    await signOut();
    void navigate('/entrar', { replace: true });
  };

  const menu = (compacta: boolean) => (
    <Menu
      mode="inline"
      inlineCollapsed={compacta}
      style={{ borderInlineEnd: 'none' }}
      selectedKeys={[selecionado]}
      onClick={({ key }) => {
        setGavetaAberta(false);
        if (key === 'inicio') return void navigate('/');
        if (key.startsWith('rota:')) return void navigate(key.replace('rota:', ''));
        void navigate(`/estudar?module=${key.replace('modulo:', '')}`);
      }}
      items={[
        { key: 'inicio', icon: <HomeOutlined />, label: 'Início' },
        ...(compacta ? itensAchatados : gruposDeConcurso),
      ]}
    />
  );

  /*
   * Navegar a partir do rodapé da barra **fecha a gaveta**, como o menu já
   * fazia. No celular ela cobre a tela inteira: sem isto, a pessoa toca em
   * "Ver desempenho", a tela muda atrás e ela continua olhando para o menu.
   */
  const irPara = (rota: string) => {
    setGavetaAberta(false);
    void navigate(rota);
  };

  /**
   * O atalho do Desempenho, logo acima da chamada da oferta.
   *
   * É o padrão que a Voa usa no pé da barra: as linhas quietas de sempre
   * ("Ganhe R$300 indicando", "Ajuda e suporte") acima do botão colorido que
   * vende. Aqui a linha quieta leva ao Desempenho, que estava só dentro de
   * Configurações e na tela de início - dois cliques de distância de quem está
   * estudando.
   *
   * `type="text"`, e não `default`: uma borda ali competiria com a chamada
   * logo abaixo, que é o que precisa ser visto primeiro.
   */
  const desempenho = (compacta: boolean) =>
    compacta ? (
      <Tooltip title="Ver desempenho" placement="right">
        <Button
          type="text"
          icon={<LineChartOutlined />}
          aria-label="Ver desempenho"
          onClick={() => irPara('/configuracoes/desempenho')}
          style={{ margin: '0 auto 4px', display: 'block' }}
        />
      </Tooltip>
    ) : (
      <Button
        type="text"
        icon={<LineChartOutlined />}
        onClick={() => irPara('/configuracoes/desempenho')}
        /*
         * `width: 'auto'` em vez de `block`: dentro da coluna da barra o botão
         * já estica sozinho, e o `block` (que é `width: 100%`) somado às
         * margens o fazia passar oito pixels da borda direita.
         */
        style={{ margin: '0 8px 4px', justifyContent: 'flex-start', width: 'auto' }}
      >
        Ver desempenho
      </Button>
    );

  const perfil = (compacta: boolean) => (
    <PerfilDaBarra
      nome={sessao?.user.name}
      email={sessao?.user.email}
      foto={sessao?.user.image ?? undefined}
      /*
        O plano se anuncia no rodapé da barra, e não só na faixa do topo: a
        faixa se dispensa e some na tela de Plano, e o selo é o que continua
        dizendo, em toda tela, por que a cota da semana acaba - ou que ela não
        existe mais.
      */
      etiqueta={
        aviso.gratuito
          ? { rotulo: 'Grátis' }
          : aviso.assinante
            ? { rotulo: 'Pro', premium: true }
            : undefined
      }
      recolhida={compacta}
      onSair={() => void sair()}
      itensExtras={[
        {
          key: 'configuracoes',
          icon: <SettingOutlined />,
          label: 'Configurações',
          onClick: () => irPara('/configuracoes'),
        },
      ]}
    />
  );

  return (
    /*
      A oferta envolve o app inteiro porque **qualquer tela vende**: o bloqueio
      no meio de uma questão, a faixa do topo, o menu, a tela de Plano. Com o
      modal aqui, todas elas chamam `abrirOferta()` e a pessoa continua onde
      estava - que é o que os quatro `navigate` para a vitrine tiravam dela.
    */
    <OfertaProvider>
      {/*
        As boas-vindas ficam no layout, e não numa página, porque a pessoa não
        aterrissa sempre no mesmo lugar depois do cadastro: quem clicou em
        "Comprar o Enac" na landing chega em `/configuracoes/plano`.
      */}
      <BoasVindas />

      {/*
        A faixa é do topo da **janela**, não do conteúdo: ela passa por cima da
        barra lateral também, como na Voa. É o que faz dela um aviso do produto,
        e não mais um cartão dentro da página.
      */}
      {aviso.mostrar ? <AvisoDoPlanoGratuito {...aviso} /> : null}

      <Layout style={{ minHeight: `calc(100dvh - ${recuoDoTopo}px)` }}>
        {telas.md ? (
          <Layout.Sider
            theme="light"
            width={LARGURA}
            collapsedWidth={LARGURA_RECOLHIDA}
            collapsible
            collapsed={recolhida}
            onCollapse={(valor, tipo) =>
              tipo === 'responsive' ? recolherPorTela(valor) : escolher(valor)
            }
            breakpoint="lg"
            trigger={null}
            style={{
              background: token.colorBgContainer,
              borderInlineEnd: `1px solid ${token.colorSplit}`,
              // Fixa: a navegação não vai embora ao rolar centenas de questões.
              position: 'fixed',
              insetInlineStart: 0,
              // Começa abaixo da faixa do plano, quando ela está no ar.
              top: recuoDoTopo,
              bottom: 0,
              height: `calc(100dvh - ${recuoDoTopo}px)`,
            }}
          >
            <Flex vertical style={{ height: '100%' }}>
              <CabecalhoDaBarra recolhida={recolhida} aoAlternar={escolher} />

              {/* O menu rola sozinho se crescer; o perfil fica colado embaixo. */}
              <div style={{ flex: 1, overflowY: 'auto' }}>{menu(recolhida)}</div>

              <RodapeDaBarra>
                {desempenho(recolhida)}
                {/*
                  **A chamada fica de pé para todo aluno gratuito**, com a faixa
                  do topo no ar ou não.

                  Em 18/09/2026 ela passou a sumir enquanto a faixa aparecia -
                  as duas dizem a mesma frase, "restam N questões nesta semana",
                  e na tela do Enac há ainda a cota no cabeçalho. O Calebe pediu
                  a volta no mesmo dia, e a razão é mais forte que a repetição:
                  **a faixa se dispensa e a cota é de uma tela só; esta é a
                  única porta de compra que está sempre no mesmo lugar.** Sumir
                  justamente na primeira visita - quando a faixa ainda não foi
                  dispensada - tirava o caminho de quem ainda está decidindo.
                */}
                {aviso.gratuito ? (
                  <ChamadaDoPlano recolhida={recolhida} restante={aviso.restante} />
                ) : null}
                {perfil(recolhida)}
              </RodapeDaBarra>
            </Flex>
          </Layout.Sider>
        ) : (
          <Drawer
            placement="left"
            size={260}
            open={gavetaAberta}
            onClose={() => setGavetaAberta(false)}
            title={<Logotipo altura={22} />}
            /*
             * **A gaveta usa o fundo da barra, não o de modal.** O Antd pinta o
             * painel dela com `colorBgElevated`, que no escuro é `#1f1f1f` -
             * mais claro que o `#141414` do menu que mora dentro. O resultado
             * era uma moldura cinza em volta do menu, como se fossem duas
             * superfícies; no claro os dois são brancos e ninguém via.
             */
            styles={{
              section: { background: token.colorBgContainer },
              body: { padding: 0, display: 'flex', flexDirection: 'column' },
            }}
          >
            <div style={{ flex: 1, overflowY: 'auto' }}>{menu(false)}</div>
            <RodapeDaBarra>
              {desempenho(false)}
              {aviso.gratuito ? (
                <ChamadaDoPlano
                  recolhida={false}
                  restante={aviso.restante}
                  antesDeAbrir={() => setGavetaAberta(false)}
                />
              ) : null}
              {perfil(false)}
            </RodapeDaBarra>
          </Drawer>
        )}

        <Layout
          style={{
            // Acompanha a barra fixa, que saiu do fluxo do documento.
            marginInlineStart: telas.md ? (recolhida ? LARGURA_RECOLHIDA : LARGURA) : 0,
            transition: 'margin-inline-start 0.2s',
          }}
        >
          {telas.md ? null : (
            <Layout.Header
              style={{
                /*
                  Logotipo centralizado, botao do menu ancorado na esquerda.
                  Com o logotipo colado no botao, a barra inteira lia como um
                  bloco de controle; centralizado, ele volta a ser assinatura da
                  marca, e o polegar continua achando o menu no canto de sempre.

                  O botao sai do fluxo de proposito: no fluxo, o logotipo
                  centralizaria no espaco que sobra e ficaria deslocado.
                */
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingInline: 12,
                background: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorSplit}`,
              }}
            >
              <Button
                type="text"
                aria-label="Abrir menu"
                icon={<MenuOutlined />}
                onClick={() => setGavetaAberta(true)}
                style={{ position: 'absolute', insetInlineStart: 8 }}
              />
              {/*
                Grande, como na Voa. No celular o cabecalho e a unica marca
                visivel na tela - a barra lateral virou gaveta -, e um logotipo
                de 24px ali lia como rodape, nao como topo de produto.

                O numero nao mudou com o recorte dos mestres, mas o que ele mede
                mudou: antes eram 34px de moldura com 13,9 de arte dentro; agora
                sao 34 de arte. E o botao do menu esta fora do fluxo, entao a
                marca centralizada tem a barra inteira para crescer.

                Com o livro no lugar do lapis o logotipo ficou 6,35 vezes mais
                largo que alto, e 34 de altura dao 215,8 de largura. Numa tela
                de 320 a marca centralizada vai de 52,1 a 267,9, e o botao do
                menu acaba em 40: sobram 12px entre os dois. Era o dobro disso
                antes. Se a altura subir daqui, e essa folga que acaba primeiro.
              */}
              <Logotipo altura={34} />
            </Layout.Header>
          )}

          {/*
            Coluna de leitura confortável nas telas de estudo; largura inteira em
            Configurações, que tem navegação própria e cuida do próprio recuo.
          */}
          {/*
            A coluna de leitura **estica até o fim da tela**, e não só até onde
            o conteúdo vai. Sem isso, uma página que quer centralizar na
            vertical - a de início é a primeira - teria de adivinhar a altura da
            moldura, que muda quando a faixa do plano aparece. Com a coluna
            esticada, um `flex: 1` lá dentro centraliza sozinho.
          */}
          <Layout.Content
            style={{
              padding: telaLarga ? 0 : 16,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {telaLarga ? (
              <Outlet />
            ) : (
              <div
                style={{
                  maxWidth: LARGURA_DE_LEITURA,
                  marginInline: 'auto',
                  width: '100%',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Outlet />
              </div>
            )}
          </Layout.Content>
        </Layout>
      </Layout>
    </OfertaProvider>
  );
}
